import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import ProductForm from '../components/products/ProductForm'
import productService from '../services/productService'
import categoryService from '../services/categoryService'
import { showSuccess, showError } from '../utils/notificationUtils'
import { Category } from '../types/categories'
import { Spin } from 'antd'

const ProductEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [product, setProduct] = useState<any>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const isFirstRender = useRef(true)

  useEffect(() => {
    const fetchData = async () => {
      if (!isFirstRender.current) return
      isFirstRender.current = false

      try {
        // Fetch categories
        const categoriesRes = await categoryService.getCategories()
        setCategories(categoriesRes.data)

        if (id) {
          const response = await productService.getProductById(id)

          const productData = {
            ...response.data,
            price_regular: response.data.price?.regular || 0,
            price_sale: response.data.price?.sale || 0,
            quillValue: response.data.description.full || '',
            description_full: response.data.description.full || '',
            description_short: response.data.description.short || '',
            sizes: response.data.sizes || [],
            category: response.data.category?._id || response.data.category || '',
            images: response.data.images || [],
            material: response.data.material || '',
            purposes: response.data.purposes || [],
            user: response.data.user || '',
            colors: response.data.colors || [],
            weaving: response.data.weaving || '',
            printing: response.data.printing || '',
            finishing: response.data.finishing || '',
            care: response.data.care || '',
            creative_note: response.data.creative_note || ''
          }
          // console.log('productData', productData)
          setProduct(productData)
        }
      } catch (err: any) {
        setError(err.message || 'Không thể tải thông tin sản phẩm')
        showError('Không thể tải thông tin sản phẩm')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [id])

  const handleSubmit = async (values: any) => {
    if (!product?._id) return

    try {
      setIsSubmitting(true)
      setError(null)

      const updateData = {
        ...values,
        _id: product._id,
        price: (() => {
          // Ưu tiên lấy từ values.price trước, sau đó mới từ values.price_regular/price_sale
          const regular = values.price?.regular || values.price_regular || 0;
          const sale = values.price?.sale || values.price_sale || 0;
          return {
            regular: Number(regular),
            sale: Number(sale)
          };
        })(),
        description: {
          short: values.description.short || '',
          full: values.quillValue || values.description.full || ''
        },
        sizes: (() => {
          const sizes = values.sizes || [];
          // Nếu sizes là mảng object, chỉ lấy value
          if (Array.isArray(sizes) && sizes.length > 0 && typeof sizes[0] === 'object') {
            return sizes.map((size: any) => size.value || size);
          }
          return sizes;
        })(),
        images: values.images || [],
        tags: values.tags || [],
        material: values.material || '',
        purposes: (() => {
          const purposes = values.purposes || [];
          // Nếu purposes là mảng object, chỉ lấy value
          if (Array.isArray(purposes) && purposes.length > 0 && typeof purposes[0] === 'object') {
            return purposes.map((purpose: any) => purpose.value || purpose);
          }
          return purposes;
        })(),
        user: values.user || '',
        colors: values.colors || [],
      }

      // console.log('updateData', updateData)
      const response = await productService.updateProduct(product._id, updateData)

      if (response.success) {
        showSuccess('Cập nhật sản phẩm thành công')
        navigate('/admin/products')
      } else {
        setError(response.message || 'Có lỗi xảy ra khi cập nhật sản phẩm')
      }
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi cập nhật sản phẩm')
      showError('Cập nhật sản phẩm thất bại')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/products')
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <Spin />
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Link
            to="/admin/products"
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            <ArrowLeftIcon className="mr-1 w-4 h-4" />
            <span>Quay lại danh sách sản phẩm</span>
          </Link>
        </div>
        <div className="p-4 text-red-700 bg-red-100 rounded-lg">
          Không tìm thấy thông tin sản phẩm
        </div>
      </div>
    )
  }

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <Link
          to='/admin/products'
          className='inline-flex items-center text-blue-600 hover:text-blue-800'
        >
          <ArrowLeftIcon className='mr-1 w-4 h-4' />
          <span>Quay lại danh sách sản phẩm</span>
        </Link>
        <h1 className='mt-4 text-2xl font-semibold text-gray-900'>
          Chỉnh sửa sản phẩm
        </h1>
        <p className='mt-1 text-sm text-gray-500'>
          Chỉnh sửa thông tin sản phẩm "{product.name}"
        </p>
      </div>

      {error && (
        <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}

      <ProductForm
        initialValues={{
          ...product,
          images: product.images || [], // Đảm bảo có mảng ảnh ban đầu
          category: product.category, // Đã được parse đúng ở trên
          material: product.material || '',
          purposes: product.purposes || [],
          user: product.user || '',
          colors: product.colors || [],
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        categories={categories}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}

export default ProductEdit
